import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ConfigService } from '@shared-lib/shared-services/config.service';

/**
 * CustomConfigService
 *
 * Extends shared ConfigService to allow project-specific payload overrides.
 */
@Injectable({
  providedIn: 'root',
})
export class CustomConfigService extends ConfigService {
  constructor(http: HttpClient) {
    super(http);
  }

  private normalizeFilterValues(value: any): any[] {
    const flat = Array.isArray(value) ? value.flat(Infinity) : [value];
    return flat.filter(
      (v) =>
        v !== undefined &&
        v !== null &&
        v !== '' &&
        !(Array.isArray(v) && v.length === 0),
    );
  }

  private sanitizeSourceParams(filters: any): Record<string, any> {
    if (!filters) return {};

    if (typeof filters !== 'object' || Array.isArray(filters)) {
      return {};
    }

    const reservedKeys = new Set([
      'queryParams',
      'requestParams',
      'widgetId',
      'changedFilterKey',
      'filterListener',
      'legendSync',
      'selectedIndices',
      'selectedLegends',
      'selectedColors',
      'seriesNames',
      'colorSyncEnabled',
      'sourceWidgetId',
      'isCategoryFilter',
      'skipReload',
      'rawQueryString',
      'useQueryParams',
    ]);

    const sanitizeObject = (input: any): Record<string, any> => {
      if (!input || typeof input !== 'object' || Array.isArray(input)) {
        return {};
      }

      return Object.keys(input).reduce((acc: Record<string, any>, key: string) => {
        if (reservedKeys.has(key)) {
          return acc;
        }

        const value = input[key];
        if (
          value === undefined ||
          value === null ||
          value === '' ||
          (Array.isArray(value) && value.length === 0)
        ) {
          return acc;
        }

        if (key.includes(',')) {
          console.warn(`[CustomConfigService] Skipping malformed filter key (contains commas): ${key}`);
          return acc;
        }
        acc[key] = value;
        return acc;
      }, {});
    };

    const requestParams = sanitizeObject(filters.requestParams);
    const queryParams = sanitizeObject(filters.queryParams);
    const rootParams = sanitizeObject(filters);

    // Merge queryParams and requestParams so that filters from isQueryParam: true
    // components (e.g. multiselect-toggle) are also included in the custom payload.
    // requestParams takes precedence over queryParams for overlapping keys.
    const merged = { ...queryParams, ...requestParams };
    const sourceParams = Object.keys(merged).length > 0 ? merged : rootParams;

    return this.pruneSiblingTimeFilters(
      sourceParams,
      filters?.changedFilterKey,
    );
  }

  private resolveSeriesKey(data: any, sourceParams: any): string {
    let seriesKey = String(data?.seriesKey || '').trim();
    if (!seriesKey || !(seriesKey in (sourceParams || {}))) {
      const fallbackKey = Object.keys(sourceParams || {}).find(
        (k) => !['year', 'qtr', 'quarter', 'month'].includes(String(k).trim().toLowerCase()),
      );
      seriesKey = fallbackKey ? String(fallbackKey).trim() : '';
    }
    return seriesKey;
  }

  private buildStackbarSliceWhereConditions(data: any, sourceParams: any): any[] {
    const seriesKey = this.resolveSeriesKey(data, sourceParams);
    const seriesValues = seriesKey
      ? this.normalizeFilterValues(sourceParams?.[seriesKey])
      : [];
    const yearValues = this.normalizeFilterValues(sourceParams?.year);
    const enableQuarter = !!data?.chartConfig?.enableQuarterYearAxis || !!data?.enableQuarterlyPivot;
    const qtrValues = enableQuarter
      ? this.normalizeFilterValues(sourceParams?.qtr)
      : [];

    if (yearValues.length === 0) return [];
    if (enableQuarter && qtrValues.length === 0) return [];

    const groups: Array<{ series: any; year: any; qtr?: any }> = [];

    // 1) Natural zipped pairing for slice selections (series/year/qtr aligned by index)
    const hasZippableLengths = enableQuarter
      ? seriesValues.length === yearValues.length && yearValues.length === qtrValues.length
      : seriesValues.length === yearValues.length;

    if (hasZippableLengths && seriesValues.length > 0) {
      for (let i = 0; i < seriesValues.length; i++) {
        groups.push({
          series: seriesValues[i],
          year: yearValues[i],
          ...(enableQuarter ? { qtr: qtrValues[i] } : {}),
        });
      }
    } else if (seriesValues.length === 1) {
      // 2) Legend(single series) + multi year/qtr axis selection
      const pairCount = enableQuarter
        ? Math.min(yearValues.length, qtrValues.length)
        : yearValues.length;
      for (let i = 0; i < pairCount; i++) {
        groups.push({
          series: seriesValues[0],
          year: yearValues[i],
          ...(enableQuarter ? { qtr: qtrValues[i] } : {}),
        });
      }
    } else if (yearValues.length === 1) {
      // 3) Multi legend selection + single year(/qtr) axis selection
      for (let i = 0; i < seriesValues.length; i++) {
        groups.push({
          series: seriesValues[i],
          year: yearValues[0],
          ...(enableQuarter ? { qtr: qtrValues[0] } : {}),
        });
      }
    } else {
      // 4) Legend selection added after x-axis multi-select:
      // build cartesian pairs so each selected legend is combined with each selected time bucket.
      const uniqueSeries = Array.from(new Set(seriesValues));
      const yearQuarterPairs: Array<{ year: any; qtr?: any }> = [];

      if (enableQuarter) {
        if (yearValues.length === qtrValues.length) {
          // If lengths match, assume they are pairs (from axis/coordinate clicks)
          yearValues.forEach((year, idx) => {
            yearQuarterPairs.push({ year, qtr: qtrValues[idx] });
          });
        } else {
          // If lengths don't match, assume they represent independent selections (from dropdowns or mixed headers)
          // Build Cartesian product to ensure all selected years and quarters are represented.
          yearValues.forEach((year) => {
            qtrValues.forEach((qtr) => {
              yearQuarterPairs.push({ year, qtr });
            });
          });
        }
      } else {
        // Just years
        yearValues.forEach((year) => {
          yearQuarterPairs.push({ year });
        });
      }

      if (uniqueSeries.length > 0 && yearQuarterPairs.length > 0) {
        uniqueSeries.forEach((series) => {
          yearQuarterPairs.forEach((timePair: any) => {
            groups.push({
              series,
              year: timePair.year,
              ...(enableQuarter ? { qtr: timePair.qtr } : {}),
            });
          });
        });
      } else if (yearQuarterPairs.length > 0) {
        // No series selected: still build grouped time-pair payload.
        yearQuarterPairs.forEach((timePair: any) => {
          groups.push({
            series: undefined,
            year: timePair.year,
            ...(enableQuarter ? { qtr: timePair.qtr } : {}),
          });
        });
      } else {
        // Final conservative fallback: zip to shortest
        const minCount = enableQuarter
          ? Math.min(seriesValues.length, yearValues.length, qtrValues.length)
          : Math.min(seriesValues.length, yearValues.length);
        for (let i = 0; i < minCount; i++) {
          groups.push({
            series: seriesValues[i],
            year: yearValues[i],
            ...(enableQuarter ? { qtr: qtrValues[i] } : {}),
          });
        }
      }
    }

    const uniqueGroups = groups.filter((g, idx, arr) => {
      const key = `${g.series}__${g.year}__${enableQuarter ? g.qtr : ''}`;
      return arr.findIndex((x) => `${x.series}__${x.year}__${enableQuarter ? x.qtr : ''}` === key) === idx;
    });

    if (!uniqueGroups.length) return [];

    const where: any[] = [];

    if (!enableQuarter) {
      // ── Yearly view: flat IN/IN format (AND-only, no group-separator OR) ──────
      // Collects all unique series + year values across every group and emits
      // them as two flat IN conditions joined by AND.  This matches the same
      // clean payload structure that the quarterly view produces per group
      // (series AND year AND qtr), but without a quarter dimension.
      // Works for all cases: single-series+single-year, multi-series+single-year,
      // single-series+multi-year, or any combination.
      const uniqueYears = [...new Set(
        uniqueGroups.map((g) => g.year).filter((y) => y !== undefined && y !== null),
      )];
      const seriesVals = seriesKey
        ? [...new Set(
          uniqueGroups
            .map((g) => g.series)
            .filter((s) => s !== undefined && s !== null && s !== ''),
        )]
        : [];

      if (seriesVals.length > 0) {
        where.push({
          attributeName: seriesKey,
          operator: seriesVals.length > 1 ? 'IN' : '=',
          attributeValues: seriesVals,
          logicalOperator: 'AND',
        });
      }
      if (uniqueYears.length > 0) {
        where.push({
          attributeName: 'year',
          operator: 'IN',
          attributeValues: uniqueYears,
          logicalOperator: '', // normalizeWhereLogicalOperators will finalize this
        });
      }
      return where;
    }

    // ── Quarterly view: group-by-group expansion (series AND year AND qtr per group, OR between groups) ──
    uniqueGroups.forEach((group, i) => {
      const isLastGroup = i === uniqueGroups.length - 1;
      if (seriesKey && group.series !== undefined && group.series !== null && group.series !== '') {
        where.push({
          attributeName: seriesKey,
          operator: '=',
          attributeValues: [group.series],
          logicalOperator: 'AND',
        });
      }
      where.push({
        attributeName: 'year',
        operator: 'IN',
        attributeValues: [group.year],
        logicalOperator: 'AND',
      });
      where.push({
        attributeName: 'qtr',
        operator: 'IN',
        attributeValues: [group.qtr],
        logicalOperator: isLastGroup ? '' : 'OR',
      });
    });

    return where;
  }

  private normalizeWhereLogicalOperators(whereConditions: any[]): void {
    if (!Array.isArray(whereConditions) || whereConditions.length === 0) {
      return;
    }

    for (let i = 0; i < whereConditions.length - 1; i++) {
      if (!whereConditions[i]?.logicalOperator) {
        whereConditions[i].logicalOperator = 'AND';
      }
    }

    whereConditions[whereConditions.length - 1].logicalOperator = '';
  }

  override getConfig(url: string, data?: any, filters?: any): Observable<any> {
    const widgetId = String(data?.widgetId || '').trim();
    const shouldTraceQuarterFlow = this.shouldTraceQuarterFlow(url, data, filters);
    const useCustomPayload =
      widgetId === 'yearly-trend-by-region' ||
      widgetId === 'yearly-trend-by-segment' ||
      widgetId === 'yearly-trend-by-engine-family' ||
      widgetId === 'sales-by-customer-table' ||
      widgetId === 'quarterly-trend-by-region' ||
      widgetId === 'quarterly-trend-by-segment' ||
      widgetId === 'quarterly-trend-by-engine-family' ||
      widgetId === 'MSBI out Gross Sales trend by Engine Family' ||
      widgetId === 'MSBI out Gross Sales trend by Region' ||
      widgetId === 'MSBI out Gross Sales trend by Country' ||
      widgetId === 'MSBI out Gross Sales trend by Engine Model' ||
      widgetId === 'Commercial view Engine Population' ||
      widgetId === 'msbi-sales-margin-by-engine-family' ||
      widgetId === 'volume' ||
      widgetId === '$ Sales trend by Region' ||
      widgetId === '$ Sales trend by Segment' ||
      widgetId === 'Margin % by Region' ||
      widgetId === 'widget1' ||
      widgetId === 'widget2' ||
      widgetId === 'widget3' ||
      widgetId === 'margin-quarterly-trend'




    if (useCustomPayload) {
      if (shouldTraceQuarterFlow) {
        this.traceQuarterFlow('customConfigService:getConfig:customPayloadFlow:start', {
          url,
          widgetId: data?.widgetId,
          changedFilterKey: filters?.changedFilterKey,
          filters,
        });
      }

      console.log(
        `[CustomConfigService] Applying custom payload for widget: ${data.widgetId}`,
      );

      const whereConditions: any[] = [];

      if (filters) {
        const sourceParams = this.sanitizeSourceParams(filters);

        if (shouldTraceQuarterFlow) {
          this.traceQuarterFlow('customConfigService:getConfig:sourceParams', {
            url,
            widgetId: data?.widgetId,
            sourceParams,
          });
        }

        console.log('sourceParams:', sourceParams);

        const resolvedSeriesKey = this.resolveSeriesKey(data, sourceParams);
        const stackbarWhere = this.buildStackbarSliceWhereConditions(
          data,
          sourceParams,
        );

        if (stackbarWhere.length > 0) {
          whereConditions.push(...stackbarWhere);
        }

        const consumedStackbarKeys = new Set<string>(
          stackbarWhere.length > 0
            ? [resolvedSeriesKey, 'year', 'qtr'].filter(Boolean)
            : [],
        );

        const keyMapping = data?.filterKeyMap || data?.chartFilterKeyMap || {};

        Object.keys(sourceParams).forEach((key) => {
          if (consumedStackbarKeys.has(key)) {
            return;
          }
          // let value = sourceParams[key];

          // 🔥 If value is an object, extract only its actual filter value
          // if (typeof value === 'object' && !Array.isArray(value)) {
          //   value = value[key]; // extract region from { region: "...", year: "..." }
          // }

          // Skip empty values

          const value = sourceParams[key];
          if (
            value === undefined ||
            value === null ||
            value === '' ||
            (Array.isArray(value) && value.length === 0)
          ) {
            return;
          }

          const attributeValues = this.normalizeFilterValues(value);

          if (attributeValues.length === 0) {
            return;
          }

          const mappedKey = keyMapping[key] || key;
          whereConditions.push({
            attributeName: mappedKey,

            // Use IN when multiple values for the same attribute, = for single
            operator: this.getPayloadFilterOperator(mappedKey, attributeValues),
            attributeValues,
            logicalOperator: 'AND', // will be corrected below
          });
        });

        this.normalizeWhereLogicalOperators(whereConditions);
      }

      const normalizedWhereConditions = this.normalizeTimeFilterWhere(whereConditions);
      const customPayload = {
        filter: {
          where: normalizedWhereConditions,
          join: {},
          orderBy: {},
        },
      };

      data.customPayload = {
        ...customPayload,
      };
      data.skipDefaultPayload = true;

      console.log(
        `[CustomConfigService] Final customPayload:`,
        data.customPayload,
      );

      if (shouldTraceQuarterFlow) {
        this.traceQuarterFlow('customConfigService:getConfig:customPayloadBuilt', {
          url,
          widgetId: data?.widgetId,
          customPayload: data.customPayload,
        });
      }

      // Prevent duplication: Clone filters and remove year/qtr before calling base getConfig
      if (filters?.requestParams || filters?.queryParams) {
        const filtersClone = JSON.parse(JSON.stringify(filters));
        
        if (filtersClone.requestParams) {
          delete filtersClone.requestParams['year'];
          delete filtersClone.requestParams['qtr'];
          delete filtersClone.requestParams['quarter'];
        }
        
        if (filtersClone.queryParams) {
          delete filtersClone.queryParams['year'];
          delete filtersClone.queryParams['qtr'];
          delete filtersClone.queryParams['quarter'];
        }

        if (shouldTraceQuarterFlow) {
          this.traceQuarterFlow('customConfigService:getConfig:filtersCloneForBaseCall', {
            url,
            widgetId: data?.widgetId,
            filtersClone,
          });
        }

        return super.getConfig(url, data, filtersClone);
      }
    }

    // Always call parent
    return super.getConfig(url, data, filters);
  }
}
